import { useEffect, useRef } from 'react'

// Cloudflare Turnstile for the staff sign-in pages (CMS admin and super console, #228). The CSP of both apps allows
// exactly this script and its iframe (infra/spa-headers.mjs).

type TurnstileApi = {
  render(el: HTMLElement, options: Record<string, unknown>): string
  reset(widgetId?: string): void
  remove(widgetId?: string): void
}

const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

const turnstile = () => (window as { turnstile?: TurnstileApi }).turnstile

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (turnstile()) return resolve()
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]')
    const script = existing ?? Object.assign(document.createElement('script'), { src: SRC, async: true })
    script.dataset.turnstile = '1'
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', () => reject(new Error('turnstile')), { once: true })
    if (!existing) document.head.appendChild(script)
  })
}

/**
 * The check box. The current token is reported through `onToken` ('' when there is none); a token works once, so a
 * change of `resetKey` (after a failed sign-in) asks for a fresh one. `language` follows the UI (`en`, `id`).
 */
export function Turnstile({ siteKey, action, language, onToken, resetKey }: { siteKey: string; action: string; language: string; onToken: (token: string) => void; resetKey: unknown }) {
  const box = useRef<HTMLDivElement>(null)
  const widget = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void loadScript()
      .then(() => {
        const api = turnstile()
        if (cancelled || !box.current || !api || widget.current) return
        widget.current = api.render(box.current, {
          sitekey: siteKey,
          action,
          language,
          size: 'flexible',
          callback: (t: string) => onToken(t),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
        })
      })
      .catch(() => undefined) // blocked script: no token, and the server refuses the sign-in with a clear message
    return () => {
      cancelled = true
      if (widget.current) turnstile()?.remove(widget.current)
      widget.current = null
      onToken('')
    }
  }, [siteKey, action, language, onToken])

  useEffect(() => {
    onToken('')
    if (widget.current) turnstile()?.reset(widget.current)
  }, [resetKey, onToken])

  return <div ref={box} className="min-h-[65px]" />
}
